// Workspace selection utilities
//
// In plugin mode (Eclipse / VS Code) browser localStorage may be wiped when the
// webview reloads (SWT Browser on Eclipse does not guarantee persistence).
// To work around this, workspace selection is *also* persisted through the
// bridge's native storage (Java Preferences / VS Code SecretStorage) and
// hydrated back into localStorage on startup via `initWorkspaceStorage()`.

import { bridge } from '~/services/bridge'
import { shell } from '~/services/bridge/thinShellClient'

export interface OrgMetadataSSO {
  sso_client_id: string
  sso_callback_url: string
  sso_client_secret: string
  sso_tenant_id: string
  sso_type: string
}

export interface OrgRuntimeSetting {
  java_version?: string
  maven_version?: string
  mule_runtime_version?: string
  settings_xml_raw_txt?: string
  settings_security_xml_raw_txt?: string
  settings_xml_file_name?: string
  settings_security_xml_file_name?: string
}

export interface Workspace {
  id: number
  uuid: string
  name: string
  is_default: boolean
  org_metadata_sso: OrgMetadataSSO
  org_session_timeout: number
  org_runtime_setting: OrgRuntimeSetting
  user_role: string
  is_admin: boolean
  account_type: string
  onboarded: boolean
  onboard_started: boolean
  /** True when this workspace must have its own valid settings.xml uploaded
   *  before the user can proceed — forced onboarding for any workspace still on
   *  a shared/Curie-managed settings.xml, whatever its age. Missing/false means
   *  today's optional, dismissible onboarding behavior. */
  settings_xml_required?: boolean
}

const STORAGE_KEY = 'curie_workspace_selection'
const BRIDGE_STORAGE_KEY = 'curie_workspace_selection'

interface WorkspaceStorage {
  [billingAccountId: string]: {
    selectedWorkspaceUuid: string
    lastUsed: number
  }
}

/**
 * In plugin mode, restore workspace selection from bridge storage into
 * localStorage so that the synchronous `getStoredWorkspace` picks it up.
 * Call once before the first `selectWorkspace` invocation (e.g. in the app
 * bootstrap or WorkspaceProvider).
 */
export async function initWorkspaceStorage(): Promise<void> {
  if (!bridge.isInPlugin()) return

  try {
    const persisted = await shell.getStorageItem(BRIDGE_STORAGE_KEY)
    if (persisted) {
      localStorage.setItem(STORAGE_KEY, persisted)
    }
  } catch (error) {
    console.warn('[workspaceSelection] Failed to hydrate from bridge storage:', error)
  }
}

/**
 * Get stored workspace preference for a billing account
 */
export function getStoredWorkspace(billingAccountId: number): string | null {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    if (!stored) return null

    const data: WorkspaceStorage = JSON.parse(stored)
    const accountData = data[billingAccountId.toString()]

    return accountData?.selectedWorkspaceUuid || null
  } catch (error) {
    console.warn('Failed to get stored workspace:', error)
    return null
  }
}

/**
 * Store workspace preference for a billing account.
 * Writes to localStorage (fast, synchronous) and, in plugin mode, also
 * persists to bridge storage (durable across webview reloads).
 */
export function storeWorkspaceSelection(billingAccountId: number, workspaceUuid: string): void {
  try {
    const stored = localStorage.getItem(STORAGE_KEY)
    const data: WorkspaceStorage = stored ? JSON.parse(stored) : {}

    data[billingAccountId.toString()] = {
      selectedWorkspaceUuid: workspaceUuid,
      lastUsed: Date.now()
    }

    const serialized = JSON.stringify(data)
    localStorage.setItem(STORAGE_KEY, serialized)

    if (bridge.isInPlugin()) {
      shell.setStorageItem(BRIDGE_STORAGE_KEY, serialized).catch(error => {
        console.warn('[workspaceSelection] Failed to persist to bridge storage:', error)
      })
    }
  } catch (error) {
    console.warn('Failed to store workspace selection:', error)
  }
}

/**
 * Select workspace based on preference or default logic
 */
export function selectWorkspace(
  billingAccountId: number,
  workspaces: Workspace[],
  urlWorkspaceUuid?: string | null
): Workspace | null {
  if (!workspaces.length) {
    return null
  }

  // First priority: URL workspace UUID (if provided and valid)
  if (urlWorkspaceUuid) {
    const urlWorkspace = workspaces.find(w => w.uuid === urlWorkspaceUuid)
    if (urlWorkspace) {
      return urlWorkspace
    }
    // URL workspace UUID is invalid, will fall back to other options
    console.warn(`Workspace UUID ${urlWorkspaceUuid} from URL not found in available workspaces`)
  }

  // Second priority: Try to get stored preference
  const storedWorkspaceUuid = getStoredWorkspace(billingAccountId)
  if (storedWorkspaceUuid) {
    const storedWorkspace = workspaces.find(w => w.uuid === storedWorkspaceUuid)
    if (storedWorkspace) {
      return storedWorkspace
    }
  }

  // Fall back to default workspace or first workspace
  const defaultWorkspace = workspaces.find(w => w.is_default)
  return defaultWorkspace || workspaces[0]
}

/**
 * Validate if a workspace UUID exists in the list of workspaces
 */
export function isValidWorkspaceUuid(workspaces: Workspace[], workspaceUuid: string): boolean {
  return workspaces.some(w => w.uuid === workspaceUuid)
}

/**
 * Get workspace UUID for navigation
 */
export function getWorkspaceNavigationUrl(workspace: Workspace): string {
  return `/w/${workspace.uuid}`
}
